/**
 * SwapContactsModal — mirrors Angular ApplicantInfoSwapComponent (frompage='users').
 * Lets an admin swap email/mobile between two users.
 * API: POST /api/swap_applicant_info
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import axios from 'axios'
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { Loader2, ArrowLeftRight, User, Mail, Phone, SlidersHorizontal, ArrowDownUp } from 'lucide-react'
import type { UserListItem, SwapContactsModalProps, SwapContactsUserOption } from '../types'

export function SwapContactsModal({ isOpen, onClose, onSuccess }: SwapContactsModalProps) {
  const [user1, setUser1] = useState<SwapContactsUserOption | null>(null)
  const [user2, setUser2] = useState<SwapContactsUserOption | null>(null)
  const [results1, setResults1] = useState<SwapContactsUserOption[]>([])
  const [results2, setResults2] = useState<SwapContactsUserOption[]>([])
  const [swapEmail, setSwapEmail] = useState(false)
  const [swapMobile, setSwapMobile] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [loading1, setLoading1] = useState(false)
  const [loading2, setLoading2] = useState(false)

  const userMapRef = useRef<Map<string, SwapContactsUserOption>>(new Map())
  const abortController1Ref = useRef<AbortController | null>(null)
  const abortController2Ref = useRef<AbortController | null>(null)

  const searchUsers = async (
    term: string,
    setter: (u: SwapContactsUserOption[]) => void,
    setLoading: (v: boolean) => void,
    abortRef: React.MutableRefObject<AbortController | null>,
  ) => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    try {
      const res = await apiClient.get<any>(API_ENDPOINTS.USER_LIST, {
        params: term ? { username: term, perPage: 50 } : { perPage: 50 },
        signal: controller.signal,
      })
      const rawData = res?.data?.data ?? res?.data ?? res
      const rawList: UserListItem[] = Array.isArray(rawData) ? rawData : []
      const mapped = rawList
        .filter((u) => u && u.user_id != null)
        .map((u: UserListItem) => {
          const uAny = u as UserListItem & { first_name?: string; last_name?: string }
          const fullName = u.name || `${uAny.first_name ?? ''} ${uAny.last_name ?? ''}`.trim() || `User ${u.user_id}`
          const item = { user_id: u.user_id, name: fullName, email: u.email || '' }
          userMapRef.current.set(String(u.user_id), item)
          return item
        })
      setter(mapped)
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError' || axios.isCancel(err)) {
        return
      }
      setter([])
      showApiError(err)
    } finally {
      if (abortRef.current === controller) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!isOpen) {
      if (abortController1Ref.current) abortController1Ref.current.abort()
      if (abortController2Ref.current) abortController2Ref.current.abort()
      setUser1(null)
      setUser2(null)
      setResults1([])
      setResults2([])
      setSwapEmail(false)
      setSwapMobile(false)
    } else {
      void searchUsers('', setResults1, setLoading1, abortController1Ref)
      void searchUsers('', setResults2, setLoading2, abortController2Ref)
    }
  }, [isOpen])

  const handleSearch1 = (term: string) => searchUsers(term, setResults1, setLoading1, abortController1Ref)
  const handleSearch2 = (term: string) => searchUsers(term, setResults2, setLoading2, abortController2Ref)

  const options1 = useMemo(
    () =>
      results1.map((u) => ({
        label: `${u.name} : ${u.email}`,
        value: String(u.user_id),
      })),
    [results1],
  )

  const options2 = useMemo(
    () =>
      results2.map((u) => ({
        label: `${u.name} : ${u.email}`,
        value: String(u.user_id),
      })),
    [results2],
  )

  const handleSubmit = async () => {
    if (!user1 || !user2) return
    if (!swapEmail && !swapMobile) return

    setProcessing(true)
    try {
      const payload = {
        swap_type_email: swapEmail ? true : null,
        swap_type_mobile: swapMobile ? true : null,
        user1: user1.user_id,
        user2: user2.user_id,
        swap_applicants: [user1.user_id, user2.user_id],
      }
      await apiClient.post(API_ENDPOINTS.SWAP_APPLICANT_INFO, payload)
      showSuccess('User Info Swapped Successfully!')
      onSuccess()
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  const canSubmit = !!user1 && !!user2 && (swapEmail || swapMobile) && !processing

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="User Info Swap"
      headerVariant="iconTitle"
      titleIcon={<ArrowLeftRight className="h-5 w-5 text-[rgb(21_151_255)]" />}
      headerClassName="!px-5 sm:!px-6"
      onClose={onClose}
      maxWidth="max-w-lg"
      bodyScrollOnly
      overlayDataTestId="um-swap-contacts-overlay"
      panelDataTestId="um-swap-contacts-modal"
      closeButtonDataTestId="um-swap-contacts-close-btn"
      titleDataTestId="um-swap-contacts-title"
      footer={
        <div className="flex items-center justify-center gap-3 w-full" data-testid="um-swap-contacts-footer">
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'h-10 px-5 text-sm font-semibold rounded-xl')}
            data-testid="um-swap-contacts-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(primaryEntityActionButtonClass, 'h-10 px-6 text-sm font-semibold rounded-xl')}
            data-testid="um-swap-contacts-submit-btn"
          >
            {processing && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
            {processing ? 'Swapping...' : 'Submit'}
          </button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="um-swap-contacts-subtitle">
          Select Two Users to Swap Contact Info
        </p>

        {/* Users Card Container */}
        <div className="rounded-xl border border-slate-200/90 bg-slate-50/50 p-4 space-y-3 shadow-2xs">
          {/* User 1 */}
          <div className="min-w-0">
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600">
              <User className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <span>Select First User</span>
            </label>
            <SearchableSelect
              options={options1}
              value={user1 ? String(user1.user_id) : ''}
              onChange={(val) => {
                const selected = userMapRef.current.get(String(val)) ?? results1.find((u) => String(u.user_id) === val)
                setUser1(selected ?? null)
              }}
              placeholder="All User"
              searchPlaceholder="Search By Name, Email"
              onSearch={handleSearch1}
              onOpen={() => {
                if (results1.length === 0) handleSearch1('')
              }}
              invokeSearchOnClose={false}
              loading={loading1}
              triggerDataTestId="um-swap-contacts-user1-input"
            />
          </div>

          {/* Swap Indicator Divider */}
          <div className="flex items-center justify-center py-0.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white shadow-2xs">
              <ArrowDownUp className="h-3.5 w-3.5 text-blue-600" />
            </div>
          </div>

          {/* User 2 */}
          <div className="min-w-0">
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600">
              <User className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <span>Select Second User</span>
            </label>
            <SearchableSelect
              options={options2}
              value={user2 ? String(user2.user_id) : ''}
              onChange={(val) => {
                const selected = userMapRef.current.get(String(val)) ?? results2.find((u) => String(u.user_id) === val)
                setUser2(selected ?? null)
              }}
              placeholder="All User"
              searchPlaceholder="Search By Name, Email"
              onSearch={handleSearch2}
              onOpen={() => {
                if (results2.length === 0) handleSearch2('')
              }}
              invokeSearchOnClose={false}
              loading={loading2}
              triggerDataTestId="um-swap-contacts-user2-input"
            />
          </div>
        </div>

        {/* Swap Info Type Container */}
        <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/80 to-white p-4 space-y-2.5 shadow-2xs">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
            <SlidersHorizontal className="h-3.5 w-3.5 text-blue-600 shrink-0" />
            <span>Select Swap Info Type</span>
          </p>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <label
              className={cn(
                'flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-all cursor-pointer select-none',
                swapEmail
                  ? 'border-blue-500 bg-blue-50/80 text-blue-700 shadow-2xs ring-1 ring-blue-500/20'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50/60'
              )}
            >
              <input
                type="checkbox"
                checked={swapEmail}
                onChange={(e) => setSwapEmail(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                data-testid="um-swap-contacts-swap-email-checkbox"
              />
              <Mail className={cn('h-4 w-4 shrink-0', swapEmail ? 'text-blue-600' : 'text-slate-400')} />
              <span>Email</span>
            </label>

            <label
              className={cn(
                'flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-all cursor-pointer select-none',
                swapMobile
                  ? 'border-blue-500 bg-blue-50/80 text-blue-700 shadow-2xs ring-1 ring-blue-500/20'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50/60'
              )}
            >
              <input
                type="checkbox"
                checked={swapMobile}
                onChange={(e) => setSwapMobile(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                data-testid="um-swap-contacts-swap-mobile-checkbox"
              />
              <Phone className={cn('h-4 w-4 shrink-0', swapMobile ? 'text-blue-600' : 'text-slate-400')} />
              <span>Mobile No</span>
            </label>
          </div>
        </div>
      </div>
    </OnScreenModal>
  )
}
